import { Button, Col, Container, Row } from "react-bootstrap"
import { navigateTo } from "../../redux/router/routerActions"
import { useAppDispatch } from "../../redux/store"
import QuickForm from "../../Components/Forms/QuickForm/QuickForm"
import { convertActiveQueryToUrl } from "../../Helpers/Query/queryHelpers";
import useActiveQueryHook from "../../Hooks/UseActiveQuery/useActiveQueryHook";
import { useGetIntegrationsQuery } from "../../services/bClientApi"
import MongoPagination from "../../Components/Pagination/MongoPagination"
import { IntegrationType } from "../../Types/integrationType"
import PropertyTitleText from "../../Components/PropertyEntry/PropertyTitleText"

const providerOptions = [
    { value: 'airbnb', text: 'Airbnb' },
    { value: 'vrbo', text: 'VRBO' },
    { value: 'booking.com', text: 'Booking.com' },
]

const entityTypeOptions = [
    { value: 'property', text: 'Property' },
    { value: 'building', text: 'Building' },
]

export default function IntegrationsPage() {

    const dispatch = useAppDispatch()

    const { activeQuery } = useActiveQueryHook({
        query: {},
        limit: 10,
    })

    const {
        data,
        error,
        isLoading,
    } = useGetIntegrationsQuery({
        integrationsQuery: activeQuery.query,
        pagination: {
            page: activeQuery.page,
            limit: activeQuery.limit,
        }
    })
    const integrationsList = data?.docs || []

    const loadPage = (pageParams: { query: any, page: number, limit: number }) => {
        const destination = `/integrations?${convertActiveQueryToUrl(pageParams)}`
        dispatch(navigateTo(destination))
    }

    return (
        <Container>
            <Row>
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <h1>Integrations</h1>
                        <Button
                            variant="primary"
                            onClick={() => dispatch(navigateTo('/add-integration'))}
                        >
                            Add New Integration
                        </Button>
                    </div>
                    <hr />
                    {error && (<>{JSON.stringify(error)}<br /></>)}

                    <QuickForm
                        layout={{ xs: 2 }}
                        initialData={{
                            entityType: activeQuery.query.entityType,
                            entityId: activeQuery.query.entityId,
                            provider: activeQuery.query.provider,
                            primaryExternalId: activeQuery.query.primaryExternalId,
                        }}
                        fields={[
                            {
                                fieldName: 'entityType',
                                fieldLabel: 'Entity Type',
                                fieldType: 'select',
                                placeholder: '- All -',
                                options: entityTypeOptions,
                                layout: { xs: 12, md: 6 },
                            },
                            {
                                fieldName: 'entityId',
                                fieldLabel: 'Entity ID',
                                fieldType: 'text',
                                placeholder: 'Search by entity ID...',
                                layout: { xs: 12, md: 6 },
                            },
                            {
                                fieldName: 'provider',
                                fieldLabel: 'Provider',
                                fieldType: 'select',
                                placeholder: '- All -',
                                options: providerOptions,
                                layout: { xs: 12, md: 6 },
                            },
                            {
                                fieldName: 'primaryExternalId',
                                fieldLabel: 'Primary External ID',
                                fieldType: 'text',
                                placeholder: 'Search by external ID...',
                                layout: { xs: 12, md: 6 },
                            },
                        ]}
                        disabled={isLoading}
                        onSubmit={(values: any) => {
                            const query: any = { ...values }
                            loadPage({
                                query,
                                page: 1,
                                limit: activeQuery.limit
                            })
                        }}
                    />

                    <div className="integrations-list-container">
                        {integrationsList && integrationsList.length > 0 ? (
                            <div>
                                {integrationsList.map((integration: IntegrationType, idx: number) => (
                                    <div
                                        key={`${integration._id}-${idx}`}
                                        className="border rounded p-3 mb-2 d-flex justify-content-between align-items-start"
                                    >
                                        <div>
                                            <strong>{integration.provider}</strong>
                                            {' — '}
                                            {integration.entityType}: <code>{integration.entityId}</code>
                                            {integration.entityType === 'property' && (
                                                <span className="ms-2">
                                                    (<PropertyTitleText propertyId={integration.entityId} />)
                                                </span>
                                            )}
                                            <br />
                                            <small className="text-muted">
                                                External ID: {integration.primaryExternalId}
                                                {integration.sync?.status && ` | Sync: ${integration.sync.status}`}
                                            </small>
                                        </div>
                                        <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            onClick={() => dispatch(navigateTo(`/integration/${integration._id}`))}
                                        >
                                            View
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center p-4">
                                <h5>No integrations found</h5>
                                <p>Try adjusting your search filters or add a new integration.</p>
                            </div>
                        )}
                    </div>

                    <MongoPagination
                        data={data}
                        loadPage={(pageLoadInfo: { page: number, limit?: number }) => {
                            loadPage({
                                query: activeQuery.query,
                                limit: activeQuery.limit,
                                ...pageLoadInfo
                            })
                        }}
                    />
                </Col>
            </Row>
        </Container>
    )
}
